import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { useBoards } from "../hooks/useBoards";
import Icon from "../../../../shared/components/Icon";

// Custom Icon components using Icon component
const InsightsIcon = ({ color = "currentColor" }) => (
  <Icon name="analytics" size={18} color={color} />
);

const FunnelsIcon = ({ color = "currentColor" }) => (
  <Icon name="funnel" size={18} color={color} />
);

const FlowsIcon = ({ color = "currentColor" }) => (
  <Icon name="flows" size={18} color={color} />
);

const RetentionIcon = ({ color = "currentColor" }) => (
  <Icon name="retention" size={18} color={color} />
);

const BoardIcon = ({ color = "currentColor" }) => (
  <Icon name="dashboard" size={18} color={color} />
);

const HeatmapIcon = ({ color = "currentColor" }) => (
  <Icon name="analytics" size={18} color={color} />
);

// REPORT section – exact order from Mixpanel
const REPORT_ITEMS = [
  { id: "insights", label: "Insights", icon: InsightsIcon },
  { id: "funnels", label: "Funnels", icon: FunnelsIcon },
  { id: "flows", label: "Flows", icon: FlowsIcon },
  { id: "retention", label: "Retention", icon: RetentionIcon },
];

// ANALYSIS section – Board and Heatmap
const ANALYSIS_ITEMS = [
  { id: "board", label: "Board", icon: BoardIcon },
  { id: "heatmap", label: "Heatmap", icon: HeatmapIcon },
];

export default function CreateNewDropdown({ open, onClose, anchorRef }) {
  const [isOpen, setIsOpen] = useState(!!open);
  const navigate = useNavigate();
  const { team, boardId } = useParams();
  const dropdownRef = useRef(null);
  const { createBoard } = useBoards();

  useEffect(() => {
    setIsOpen(!!open);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose, anchorRef]);

  const handleSelect = async (id) => {
    onClose?.();
    const bid = boardId || "new";

    if (id === "board") {
      // Create board and open it directly (Mixpanel-style: no intermediate page)
      try {
        const newBoard = await createBoard({ name: "Untitled", description: null });
        if (newBoard?.id) {
          navigate(`/${team}/analytics/boards/${newBoard.id}`);
        }
      } catch (err) {
        console.error("Failed to create board:", err);
      }
      return;
    }

    if (id === "heatmap") {
      // Navigate to heatmap page
      navigate(`/${team}/activity/heatmap`);
      return;
    }

    // Flows uses its own dedicated editor page
    if (id === "flows") {
      navigate(`/${team}/analytics/flow/${bid}`);
      return;
    }

    // Report types → open editor with type
    if (["insights", "funnels", "retention"].includes(id)) {
      navigate(`/${team}/analytics/boards/${bid}/editor?type=${id}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute left-0 top-full mt-1 z-[100] min-w-[240px] rounded-lg border shadow-xl py-1"
      style={{
        fontFamily: "Raleway, sans-serif",
        backgroundColor: theme.bgRaised,
        borderColor: theme.border,
        position: "absolute",
      }}
    >
      {/* REPORT */}
      <div
        className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: theme.textMuted }}
      >
        Report
      </div>
      {REPORT_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item.id)}
            className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors"
            style={{ color: theme.textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.primaryLight}15`;
              e.currentTarget.style.color = theme.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = theme.textSecondary;
            }}
          >
            <span className="flex-shrink-0">
              <Icon />
            </span>
            {item.label}
          </button>
        );
      })}

      <div className="border-t my-1" style={{ borderColor: theme.border }} />

      {/* ANALYSIS */}
      <div
        className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: theme.textMuted }}
      >
        Analysis
      </div>
      {ANALYSIS_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item.id)}
            className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors"
            style={{ color: theme.textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.primaryLight}15`;
              e.currentTarget.style.color = theme.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = theme.textSecondary;
            }}
          >
            <span className="flex-shrink-0">
              <Icon />
            </span>
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{ backgroundColor: theme.bgOverlay, color: theme.textMuted }}
              >
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
